import { Inject, Injectable } from '@angular/core';
import * as Rx from 'rxjs';
import {
  BackendDeleteErrorTypes,
  BackendDeleteReturnTypes,
  BackendFetchErrorTypes,
  BackendFetchReturnTypes,
  BackendPushErrorTypes,
  BackendPushReturnTypes,
  BackendSetErrorTypes,
  BackendSetReturnTypes,
  BackendUpdateErrorTypes,
  BackendUpdateReturnTypes,
} from '../backend.model';
import { Collections, LegacySearchCollections } from '../collections.model';
import {
  BackendDeleteRequest,
  BackendFetchRequest,
  BackendPushRequest,
  BackendSetRequest,
  BackendUpdateRequest,
} from './backend';
import { HTTP_ADAPTER } from './datastore.config';
import { HttpAdapter, ResponseData } from './datastore.interface';
import { Pagination } from './store.model';

export type ApiFetchResponse<
  K extends keyof (Collections & LegacySearchCollections)
> = ResponseData<BackendFetchReturnTypes[K], BackendFetchErrorTypes[K]>;

@Injectable()
export class ApiHttp {
  constructor(@Inject(HTTP_ADAPTER) private httpAdapter: HttpAdapter) {}

  fetch<K extends keyof Collections>(
    request: BackendFetchRequest<K>,
    pagination?: Pagination,
  ): Rx.Observable<ApiFetchResponse<K>>;

  fetch<K extends keyof (Collections & LegacySearchCollections)>(
    request: BackendFetchRequest<K>,
  ): Rx.Observable<ApiFetchResponse<K>>;

  fetch<K extends keyof (Collections & LegacySearchCollections)>(
    request: BackendFetchRequest<K>,
    pagination?: Pagination,
  ): Rx.Observable<ApiFetchResponse<K>> {
    const params = pagination
      ? {
          limit: pagination.limit,
          offset: pagination.offset,
          ...request.params,
        }
      : request.params;

    if ('method' in request && request.method === 'POST') {
      const body = {
        ...request.payload,
        ...request.params,
      };
      const options = {
        isGaf: request.isGaf,
        serializeBody: request.asFormData,
      };
      return this.httpAdapter.post(request.endpoint, body, options);
    }

    // default to `GET` if not specified
    return this.httpAdapter.get(request.endpoint, {
      params,
      isGaf: request.isGaf,
    });
  }

  post<K extends keyof Collections>(
    request: BackendPushRequest<K>,
  ): Rx.Observable<
    ResponseData<BackendPushReturnTypes[K], BackendPushErrorTypes[K]>
  >;
  post<K extends keyof Collections>(
    request: BackendSetRequest<K>,
  ): Rx.Observable<
    ResponseData<BackendSetReturnTypes[K], BackendSetErrorTypes[K]>
  >;
  post<K extends keyof Collections>(
    request: BackendUpdateRequest<K>,
  ): Rx.Observable<
    ResponseData<BackendUpdateReturnTypes[K], BackendUpdateErrorTypes[K]>
  >;
  post<K extends keyof Collections>(
    request: BackendDeleteRequest<K>,
  ): Rx.Observable<
    ResponseData<BackendDeleteReturnTypes[K], BackendDeleteErrorTypes[K]>
  >;
  post<K extends keyof Collections>(
    request:
      | BackendPushRequest<K>
      | BackendSetRequest<K>
      | BackendUpdateRequest<K>
      | BackendDeleteRequest<K>,
  ): Rx.Observable<
    | ResponseData<BackendPushReturnTypes[K], BackendPushErrorTypes[K]>
    | ResponseData<BackendSetReturnTypes[K], BackendSetErrorTypes[K]>
    | ResponseData<BackendUpdateReturnTypes[K], BackendUpdateErrorTypes[K]>
    | ResponseData<BackendDeleteReturnTypes[K], BackendDeleteErrorTypes[K]>
  > {
    const body = {
      ...request.payload,
      ...request.params,
    };
    const options = {
      isGaf: request.isGaf,
      serializeBody: request.asFormData,
    };
    return this.httpAdapter.post(request.endpoint, body, options);
  }

  put<K extends keyof Collections>(
    request: BackendUpdateRequest<K>,
  ): Rx.Observable<
    ResponseData<BackendUpdateReturnTypes[K], BackendUpdateErrorTypes[K]>
  >;
  put<K extends keyof Collections>(
    request: BackendDeleteRequest<K>,
  ): Rx.Observable<
    ResponseData<BackendDeleteReturnTypes[K], BackendDeleteErrorTypes[K]>
  >;
  put<K extends keyof Collections>(
    request: BackendUpdateRequest<K> | BackendDeleteRequest<K>,
  ): Rx.Observable<
    | ResponseData<BackendUpdateReturnTypes[K], BackendUpdateErrorTypes[K]>
    | ResponseData<BackendDeleteReturnTypes[K], BackendDeleteErrorTypes[K]>
  > {
    const body = {
      ...request.payload,
      ...request.params,
    };
    const options = {
      isGaf: request.isGaf,
      serializeBody: request.asFormData,
    };
    return this.httpAdapter.put(request.endpoint, body, options);
  }

  delete<K extends keyof Collections>(
    request: BackendDeleteRequest<K>,
  ): Rx.Observable<
    ResponseData<BackendDeleteReturnTypes[K], BackendDeleteErrorTypes[K]>
  > {
    const params = {
      ...request.payload,
      ...request.params,
    };

    const options = {
      params,
      isGaf: request.isGaf,
    };
    return this.httpAdapter.delete(request.endpoint, options);
  }
}
